import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { ArrowLeft, ArrowRight, Download, Printer, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { StepLocation, StepModel, StepServices, StepStaffing } from "./ConfiguratorSteps";
import QuoteView from "./QuoteView";
import { PUB, postJSON, downloadPost, missingResponses } from "./fq";

export const STEPS = ["Location & format", "Operating model", "Services", "Staffing", "Your quote", "Submit enquiry"];

const stepError = (cat, c, step) => {
  if (step === 1) {
    if (!c.location.city.trim()) return "Enter the proposed city";
    if (!c.location.property_status) return "Choose the property status";
    if (!c.format_id) return "Choose a centre format";
  }
  if (step === 2) {
    if (!c.model_id) return "Choose one operating model";
    const m = cat.models.find((x) => x.id === c.model_id);
    if (m?.acknowledgements.some((a) => !c.acknowledgements[a.id])) return "Please confirm the required acknowledgement";
  }
  if (step === 3) {
    const miss = missingResponses(cat, c);
    if (miss.length) return `Choose a response for: ${miss.map((s) => s.name).join(", ")}`;
  }
  if (step === 4 && c.responses.staff_sourcing === "YES" && !c.staffing.some((r) => r.role.trim() && Number(r.qty) > 0))
    return "Add at least one role, or choose No for staff sourcing";
  return null;
};

const Stepper = ({ step, maxReached, go, total }) => (
  <ol className="fq-stepper" aria-label="Quote steps">
    {STEPS.slice(0, total).map((s, i) => {
      const n = i + 1;
      return (
        <li key={s}>
          <button type="button" disabled={n > maxReached} onClick={() => go(n)} aria-current={n === step ? "step" : undefined}
            data-testid={`cfg-stepper-${n}`} className={`fq-step ${n === step ? "is-current" : n < step ? "is-done" : ""}`}>
            <span className="fq-step-num">{n}</span><span className="fq-step-label">{s}</span>
          </button>
        </li>
      );
    })}
  </ol>
);

export default function Configurator({ cat, config, setConfig, step, setStep, renderFinal, calcUrl = `${PUB}/calculate`, pdf = true }) {
  const [showMissing, setShowMissing] = useState(false);
  const [result, setResult] = useState(null);
  const [calcState, setCalcState] = useState("idle");
  const [dl, setDl] = useState(false);
  const firstInvalid = [1, 2, 3, 4].find((n) => stepError(cat, config, n));
  const maxReached = firstInvalid || 6;

  useEffect(() => {
    if (step > maxReached) setStep(maxReached);
  }, [step, maxReached, setStep]);

  useEffect(() => {
    if (step < 5) return;
    let alive = true;
    setCalcState("loading");
    const t = setTimeout(() => {
      postJSON(calcUrl, { inputs: config })
        .then((d) => alive && (setResult(d.result), setCalcState("ok")))
        .catch((e) => alive && (setCalcState("error"), toast.error(e.message)));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [step, config, calcUrl]);

  const next = () => {
    const err = stepError(cat, config, step);
    if (err) { setShowMissing(true); toast.error(err); return; }
    setShowMissing(false);
    setStep(step + 1);
    window.scrollTo({ top: document.getElementById("fq-configurator")?.offsetTop - 20 || 0, behavior: "smooth" });
  };

  const download = async () => {
    setDl(true);
    try { await downloadPost(`${PUB}/quote-pdf`, { inputs: config }, "Purnabramha_Indicative_Quote.pdf"); }
    catch (e) { toast.error(e.message); }
    setDl(false);
  };

  const body = {
    1: <StepLocation cat={cat} config={config} set={setConfig} />,
    2: <StepModel cat={cat} config={config} set={setConfig} />,
    3: <StepServices cat={cat} config={config} set={setConfig} showMissing={showMissing} />,
    4: <StepStaffing config={config} set={setConfig} />,
  }[step];

  return (
    <div id="fq-configurator" className="fq-configurator" data-testid="configurator">
      <Stepper step={step} maxReached={maxReached} go={setStep} total={6} />
      <div className="mt-8">
        <h2 className="font-display text-2xl md:text-3xl text-[#3A0A06] mb-1" data-testid="cfg-step-title">Step {step}. {STEPS[step - 1]}</h2>
        {step <= 4 && body}
        {step >= 5 && (
          calcState === "loading" && !result ? <div className="py-16 flex justify-center" data-testid="cfg-calc-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : calcState === "error" && !result ? <div className="fq-callout danger">We could not calculate your quote. Your selections are kept — <button className="underline" onClick={() => setStep(5)}>try again</button>.</div>
          : result && (
            step === 5 ? (
              <div className="space-y-6 print-area">
                <div className="flex flex-wrap gap-2 no-print">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="cfg-edit-choices"><Pencil className="w-4 h-4 mr-2" />Edit choices</Button>
                  <Button variant="outline" onClick={() => window.print()} data-testid="cfg-print-quote"><Printer className="w-4 h-4 mr-2" />Print</Button>
                  {pdf && <Button variant="outline" onClick={download} disabled={dl || !result.validation.complete} data-testid="cfg-download-quote">{dl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Download PDF</Button>}
                </div>
                <p className="text-sm opacity-80">Indicative quote for <b>{result.format.label}</b> · <b>{result.model.label}</b> in {result.location.city}. Not saved and no enquiry is created until you submit.</p>
                <QuoteView result={result} />
              </div>
            ) : renderFinal({ result })
          )
        )}
      </div>
      <div className="fq-nav no-print">
        <Button variant="ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="cfg-back"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
        {step < 5 && <Button className="fq-cta" onClick={next} data-testid="cfg-next">{step === 4 ? "View my quote" : "Continue"}<ArrowRight className="w-4 h-4 ml-2" /></Button>}
        {step === 5 && <Button className="fq-cta" onClick={() => setStep(6)} disabled={!result?.validation?.complete} data-testid="cfg-to-enquiry">Continue to enquiry<ArrowRight className="w-4 h-4 ml-2" /></Button>}
      </div>
    </div>
  );
}
